/* eslint-disable react/no-multi-comp, react/prop-types */
import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';

// Components
import Select from './inputs/select';
import AjaxSelect from './inputs/ajax-select';
import DatePicker from './inputs/date';
import RegexInputComponent from './inputs/regex';
import FileSelector from './inputs/file';
import CheckboxGroup from './inputs/checkbox-group';
import Duration from './inputs/duration';
import Textarea from 'react-textarea-autosize';
import HelpText from './help-text/help-text';

const elementProps = [
  'elementClasses',
  'label',
  'addonLeft',
  'addonRight',
  'helpText',
  'meta'
];


export function TextInput({ className, input, meta, ...props }) {
  return (
    <Element {...props} {...meta}>
      <input {..._.omit(props, elementProps)} {...input} type="text" className={classNames(className, 'form-control')} />
    </Element>
  );
}

export function HiddenInput({ input, className, ...props }) {
  return <input {..._.omit(props, elementProps)} {...input} type="hidden" className={classNames(className, 'form-control')} />;
}

export function TextAreaInput({ className, input, meta, ...props }) {
  return (
    <Element {...props} {...meta} elementClasses={'textarea-form-element'}>
      <textarea {..._.omit(props, elementProps)} {...input} className={classNames(className, 'form-control')} />
    </Element>
  );
}

export function ExpandingTextAreaInput({ className, input, meta, ...props }) {
  return (
    <Element {...props} {...meta} elementClasses={'textarea-form-element'}>
      <Textarea {..._.omit(props, elementProps)} {...input} className={classNames(className, 'form-control')} />
    </Element>
  );
}

export function NumberInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <input
        {..._.omit(props, elementProps)}
        {...input}
        type="number"
        className={classNames(props.className, 'form-control')}
        autoComplete="off"
      />
    </Element>
  );
}

export function FileInput({ buttonLabel, meta, input, ...props }) {
  return (
    <label className="file-picker-input">
      <Element {...props} {...meta} elementClasses={'file-picker-element'} addonLeft={buttonLabel || 'Choose File'}>
        <FileSelector {..._.omit(props, elementProps)} {...input} />
      </Element>
    </label>
  );
}

export function RegexInput({ regex, input, meta, ...props }) {
  return (
    <Element {...props} {...meta}>
      <RegexInputComponent
        {...props}
        {...input}
        regex={regex}
      />
    </Element>
  );
}

export function IntegerInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <RegexInputComponent
        {...props}
        {...input}
        regex={/^([0-9]\d*)$/}
        className={classNames(props.className, 'right-align')}
        autoComplete="off"
      />
    </Element>
  );
}

export function MoneyInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta} addonLeft={props.addonLeft || '$'}>
      <RegexInputComponent
        {...props}
        {...input}
        regex={/^(\d+(\.\d{0,2})?|\.\d{0,2})$/}
        regexType="currency"
        className={classNames(props.className, 'right-align')}
        autoComplete="off"
      />
    </Element>
  );
}

export function DurationInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <Duration {..._.omit(props, elementProps)} {...input} />
    </Element>
  );
}

export function AjaxSelectInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <AjaxSelect {..._.omit(props, elementProps)} {...input} />
    </Element>
  );
}

export function SelectInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <Select {..._.omit(props, elementProps)} {...input} />
    </Element>
  );
}

export function DateInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <DatePicker {..._.omit(props, elementProps)} {...input} />
    </Element>
  );
}

export function CheckboxInput({ label, meta, input, className, ...props }) {
  return (
    <Element {...props} {...meta} elementClasses="checkbox-wrapper">
      <label className={classNames('no-select', { labelless: !label })}>
        <input
          {...props}
          {...input}
          checked={input.value === 'true' || input.value === true}
          type="checkbox"
          className={classNames(className, 'form-control')}
        />
        <span>{label}</span>
      </label>
    </Element>
  );
}

export function CheckboxGroupInput({ meta, input, ...props }) {
  return (
    <Element {...props} {...meta}>
      <CheckboxGroup {...props} input={input} />
    </Element>
  );
}

export function SingleRadioInput({ display, meta, input, elementClasses, checkedValue, label, ...props }) {
  return (
    <Element
      {...props}
      {...meta}
      elementClasses={classNames({ 'single-radio-button-wrapper': display === 'buttons' }, elementClasses)}
    >
      <div className={classNames('single-radio-inputs', { 'btn-group': display === 'buttons' })} data-toggle={display}>
        <label
          className={classNames({ btn: display === 'buttons', active: input.value === checkedValue })}
          onClick={() => input.onChange(checkedValue)}
        >
          <input {...input} value={checkedValue} checked={input.value === checkedValue} type="radio" {...props} />
          {label}
        </label>
      </div>
    </Element>
  );
}

export function RadioInput({ options, display, meta, input, elementClasses, ...props }) {
  return (
    <Element
      {...props}
      {...meta}
      elementClasses={classNames({ 'radio-button-wrapper': display === 'buttons' }, elementClasses)}
    >
      <div className={classNames('radio-inputs p-2', { 'btn-group': display === 'buttons' })} data-toggle={display}>
        {_.map(options, (option, index) =>
          <label
            key={index}
            className={classNames({ btn: display === 'buttons', active: input.value === option.value })}
            onClick={() => input.onChange(option.value)}
          >
            <input {...input} className="m-1 h-auto" value={option.value} checked={input.value === option.value} type="radio" {...props} />
            {option.text || option.label}
          </label>
        )}
      </div>
    </Element>
  );
}

export function RawField({ onChange, component: Component, value, ...props }) {
  return <Component input={{ onChange, value }} {...props} />;
}

// Internal components
export class Element extends React.Component { // eslint-disable-line react/prefer-stateless-function
  render() {
    const { touched, error, warning, children, elementClasses, label, addonLeft, helpText, active, addonRight } = this.props;
    const classes = classNames(elementClasses, 'form-element', { 'has-error': touched && error });
    const Wrapper = addonLeft || addonRight ? 'label' : 'div';
    const hasError = touched && error && error !== ' ';

    return (
      <Wrapper className={classes}>
        { label && <label className="form-label">{label}</label> }
        { hasError &&
          <div className="invalid-alert">{error}</div>
        }
        { warning && !hasError &&
          <div className="warning-alert">{warning}</div>
        }
        { (addonLeft || addonRight) ?
          <InputGroup addonLeft={addonLeft} addonRight={addonRight}>
            {children}
          </InputGroup>
          :
          children
        }
        { helpText &&
          <HelpText show={active} container={this}>{ helpText }</HelpText>
        }
      </Wrapper>
    );
  }
}

function InputGroup({ addonLeft, addonRight, children }) {
  return (
    <div className="input-group">
      { addonLeft &&
        <span className="input-group-prepend">
          {!_.isString(addonLeft) && addonLeft}
          {_.isString(addonLeft) && <span className="input-group-text">{addonLeft}</span>}
        </span>
      }
      {children}
      { addonRight &&
        <span className="input-group-append">
          {!_.isString(addonRight) && addonRight}
          {_.isString(addonRight) && <span className="input-group-text">{addonRight}</span>}
        </span>
      }
    </div>
  );
}
